import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Star, ArrowLeft, ExternalLink, ShoppingCart,
  Sparkles, Loader2, AlertCircle, LayoutTemplate, CheckCircle, MessageSquare, Flag,
} from 'lucide-react';
import Header from '../components/Layout/Header';
import ReportModal from '../components/marketplace/ReportModal';
import Footer from '../components/Layout/Footer';
import VerifiedBadge from '../components/shared/VerifiedBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const USER_KEY = 'ivisora_user';

const imgUrl = (src) => {
  if (!src) return null;
  if (src.startsWith('http')) return src;
  return `${API_URL}${src}`;
};

const getToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
const getUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); }
  catch { return null; }
};

/* ── Star Rating ─────────────────────────────────────────────────────────────── */
const StarRating = ({ rating, count, large }) => (
  <div className="flex items-center gap-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`${large ? 'w-5 h-5' : 'w-3.5 h-3.5'} ${
          i <= Math.round(rating)
            ? 'text-amber-400 fill-amber-400'
            : 'text-gray-200 fill-gray-100'
        }`}
      />
    ))}
    {count !== undefined && (
      <span className={`${large ? 'text-sm' : 'text-xs'} text-gray-500 ml-1`}>
        {rating > 0 ? `${rating.toFixed(1)} (${count} review${count !== 1 ? 's' : ''})` : 'No reviews yet'}
      </span>
    )}
  </div>
);

/* ── Related template card ─────────────────────────────────────────────────── */
const RelatedCard = ({ template, onClick }) => {
  const [imgOk, setImgOk] = useState(true);
  const thumb = imgUrl(template.thumbnail);
  const isFree = template.licensing_type === 'free' || !template.price_cents;

  return (
    <div
      onClick={() => onClick(template)}
      className="group cursor-pointer bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden"
    >
      <div className="relative bg-gray-50" style={{ aspectRatio: '4/3' }}>
        {thumb && imgOk ? (
          <img src={thumb} alt={template.name} className="w-full h-full object-cover" onError={() => setImgOk(false)} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <LayoutTemplate className="w-8 h-8 text-gray-300" />
          </div>
        )}
        <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${isFree ? 'bg-emerald-100 text-emerald-700' : 'bg-violet-100 text-violet-700'}`}>
          {isFree ? 'Free' : `$${(template.price_cents / 100).toFixed(2)}`}
        </span>
      </div>
      <div className="p-2.5">
        <p className="text-sm font-medium text-gray-900 truncate">{template.name}</p>
      </div>
    </div>
  );
};

export default function MarketplaceTemplatePage() {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [entitlement, setEntitlement] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({ average_rating: 0, review_count: 0 });
  const [canReview, setCanReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const user = getUser();
  const token = getToken();

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await fetch(`${API_URL}/api/marketplace/templates/${templateId}`, { headers });
        if (!r.ok) {
          setError(r.status === 404 ? 'Template not found.' : 'Failed to load template.');
          return;
        }
        const data = await r.json();
        setTemplate(data);

        // Check entitlement if logged in and paid template
        if (token && data.licensing_type === 'one_time') {
          const er = await fetch(`${API_URL}/api/marketplace/templates/${templateId}/entitlement`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (er.ok) setEntitlement(await er.json());
        }

        // Fetch related templates (same category or just trending)
        const catParam = data.template_category_id
          ? `&category_id=${data.template_category_id}`
          : '';
        const rr = await fetch(`${API_URL}/api/marketplace/templates?limit=4${catParam}`);
        if (rr.ok) {
          const rd = await rr.json();
          setRelated((rd.items || []).filter((t) => t.id !== templateId).slice(0, 4));
        }

        // Fetch reviews
        const reviewHeaders = token ? { Authorization: `Bearer ${token}` } : {};
        const revR = await fetch(`${API_URL}/api/marketplace/templates/${templateId}/reviews?limit=10`, { headers: reviewHeaders });
        if (revR.ok) {
          const revD = await revR.json();
          setReviews(revD.items || []);
          setReviewSummary(revD.summary || { average_rating: 0, review_count: 0 });
          setCanReview(revD.can_review || false);
          setHasReviewed(revD.has_reviewed || false);
        }
      } catch (err) {
        setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [templateId, token]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (reviewSubmitting) return;
    setReviewSubmitting(true);
    try {
      const params = new URLSearchParams({ rating: reviewRating });
      if (reviewComment.trim()) params.set('comment', reviewComment.trim());
      const r = await fetch(`${API_URL}/api/marketplace/templates/${templateId}/reviews?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const newRev = await r.json();
        setReviews(prev => [newRev, ...prev]);
        setReviewSummary(s => ({
          ...s,
          review_count: s.review_count + 1,
          average_rating: parseFloat(((s.average_rating * s.review_count + reviewRating) / (s.review_count + 1)).toFixed(2)),
        }));
        setHasReviewed(true);
        setCanReview(false);
        setReviewComment('');
        showToast('Review submitted!', 'success');
      } else {
        const d = await r.json().catch(() => ({}));
        showToast(d.detail || 'Failed to submit review.', 'error');
      }
    } catch {
      showToast('Network error.', 'error');
    }
    setReviewSubmitting(false);
  };

  /* ── CTA logic ─────────────────────────────────────────────────────────────── */
  const isPaid = template?.licensing_type === 'one_time';
  const hasPurchased = entitlement?.purchased;
  const isPro = user?.plan === 'pro' || user?.plan === 'enterprise';
  const canUseForFree = !isPaid || hasPurchased || isPro;

  const handleUseTemplate = async () => {
    if (!user) {
      showToast('Sign in to use this template.', 'warn');
      return;
    }
    setCtaLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/templates/${templateId}/instantiate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        if (r.status === 402) {
          showToast('Purchase required to use this template.', 'error');
        } else {
          showToast(err.detail || 'Failed to open template.', 'error');
        }
        return;
      }
      const data = await r.json();
      navigate(`/editor/${data.design_id}`);
    } catch {
      showToast('Network error. Please try again.', 'error');
    } finally {
      setCtaLoading(false);
    }
  };

  const handleBuyTemplate = async () => {
    if (!user) {
      showToast('Sign in to purchase this template.', 'warn');
      return;
    }
    setCtaLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/marketplace/templates/${templateId}/checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        if (r.status === 409) {
          showToast('You already own this template!', 'success');
          setEntitlement({ purchased: true });
        } else {
          showToast(err.detail || 'Checkout failed. Try again.', 'error');
        }
        return;
      }
      const data = await r.json();
      window.location.href = data.checkout_url;
    } catch {
      showToast('Network error. Please try again.', 'error');
    } finally {
      setCtaLoading(false);
    }
  };

  /* ── Render ─────────────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
          <button onClick={() => navigate('/marketplace')} className="mt-4 text-sm text-violet-600 hover:underline">
            Back to Marketplace
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const canvasW = template.canvas_width || 1080;
  const canvasH = template.canvas_height || 1080;
  const aspectRatio = `${canvasW}/${canvasH}`;
  const thumb = imgUrl(template.thumbnail);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' :
          toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
          toast.type === 'warn' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
          'bg-violet-50 text-violet-700 border border-violet-200'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-500">
          <button onClick={() => navigate('/marketplace')} className="hover:text-violet-600 transition-colors flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" />
            Marketplace
          </button>
          <span>/</span>
          <span className="text-gray-800 font-medium truncate max-w-xs">{template.name}</span>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-10">

          {/* Preview */}
          <div>
            <div
              className="w-full bg-gradient-to-br from-gray-100 to-slate-200 rounded-2xl overflow-hidden shadow-sm border border-gray-200 flex items-center justify-center"
              style={{ aspectRatio, maxHeight: '70vh' }}
            >
              {thumb ? (
                <img src={thumb} alt={template.name} className="w-full h-full object-contain" />
              ) : (
                <LayoutTemplate className="w-20 h-20 text-gray-300" />
              )}
            </div>

            {/* Tags */}
            {template.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {template.tags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => navigate(`/marketplace/browse?search=${encodeURIComponent(tag)}`)}
                    className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full hover:bg-violet-100 hover:text-violet-700 transition-colors"
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            )}

            {/* Related templates */}
            {related.length > 0 && (
              <div className="mt-10">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Related Templates</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {related.map((t) => (
                    <RelatedCard key={t.id} template={t} onClick={(t) => navigate(`/marketplace/templates/${t.id}`)} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Info panel */}
          <div className="space-y-5">
            {/* Title + price */}
            <div>
              <div className="flex items-start justify-between gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">{template.name}</h1>
                {isPaid && (
                  <span className="flex-shrink-0 text-xl font-bold text-violet-700">
                    ${(template.price_cents / 100).toFixed(2)}
                  </span>
                )}
                {!isPaid && (
                  <span className="flex-shrink-0 text-sm font-semibold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
                    Free
                  </span>
                )}
              </div>

              {/* Entitlement badge */}
              {isPaid && hasPurchased && (
                <div className="flex items-center gap-1.5 text-sm text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-lg w-fit mb-2">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-medium">You own this template</span>
                </div>
              )}

              {/* Rating */}
              <div className="mt-2">
                <StarRating
                  rating={template.average_rating || 0}
                  count={template.review_count || 0}
                  large
                />
              </div>

              {template.description && (
                <p className="mt-3 text-sm text-gray-600 leading-relaxed">{template.description}</p>
              )}
            </div>

            {/* Canvas size */}
            <div className="text-xs text-gray-500">
              Canvas: {canvasW} × {canvasH} px
            </div>

            {/* CTA */}
            <div className="space-y-2.5">
              {canUseForFree ? (
                <button
                  onClick={handleUseTemplate}
                  disabled={ctaLoading}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60 shadow"
                >
                  {ctaLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  Use Template
                </button>
              ) : (
                <>
                  <button
                    onClick={handleBuyTemplate}
                    disabled={ctaLoading}
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60 shadow"
                  >
                    {ctaLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ShoppingCart className="w-4 h-4" />
                    )}
                    Buy Template — ${(template.price_cents / 100).toFixed(2)}
                  </button>
                  <p className="text-xs text-center text-gray-500">
                    One-time purchase • Instant download
                  </p>
                </>
              )}

              {!user && (
                <p className="text-xs text-center text-gray-500">
                  <button onClick={() => navigate('/')} className="text-violet-600 hover:underline">Sign in</button> to use or purchase
                </p>
              )}

              {isPaid && !hasPurchased && isPro && (
                <p className="text-xs text-center text-violet-600 font-medium">
                  ✓ Included in your Pro plan
                </p>
              )}
            </div>

            {/* Creator */}
            {template.creator && (
              <div
                onClick={() => navigate(`/marketplace/creators/${template.creator.id}`)}
                className="cursor-pointer border border-gray-100 rounded-xl p-4 bg-white hover:border-violet-200 hover:bg-violet-50/30 transition-all"
              >
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Creator</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                    {(template.creator.display_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 text-sm inline-flex items-center gap-1">
                      {template.creator.display_name}
                      <VerifiedBadge verified={template.creator?.verified} />
                    </p>
                    {template.creator.bio && (
                      <p className="text-xs text-gray-500 truncate">{template.creator.bio}</p>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 ml-auto flex-shrink-0" />
                </div>
                {template.creator.featured_creator && (
                  <span className="mt-2 inline-block text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    ⭐ Featured Creator
                  </span>
                )}
              </div>
            )}

            {/* Stats */}
            {template.purchases_count > 0 && (
              <div className="flex gap-4 text-center">
                <div className="flex-1 bg-gray-50 rounded-xl py-3">
                  <p className="text-lg font-bold text-gray-900">{template.purchases_count}</p>
                  <p className="text-xs text-gray-500">Sales</p>
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl py-3">
                  <p className="text-lg font-bold text-gray-900">
                    {template.average_rating > 0 ? template.average_rating.toFixed(1) : '—'}
                  </p>
                  <p className="text-xs text-gray-500">Avg Rating</p>
                </div>
              </div>
            )}

            {/* License explanation */}
            <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">License</p>
              <div className="space-y-1.5">
                {[
                  { ok: true,  text: 'Personal use' },
                  { ok: true,  text: 'Commercial use' },
                  { ok: true,  text: 'Unlimited exports' },
                  { ok: false, text: 'Resale not allowed' },
                  { ok: false, text: 'Redistribution not allowed' },
                ].map(({ ok, text }) => (
                  <div key={text} className="flex items-center gap-2">
                    <span className={`text-sm font-bold leading-none ${ok ? 'text-emerald-500' : 'text-red-400'}`}>
                      {ok ? '✓' : '✗'}
                    </span>
                    <span className="text-sm text-gray-600">{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Report */}
            <div className="pt-1">
              <button
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 transition-colors"
              >
                <Flag className="w-3.5 h-3.5" /> Report template
              </button>
            </div>
          </div>
        </div>

        {/* Reviews section */}
        <div className="mt-12 pt-10 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-5 h-5 text-violet-500" />
            <h2 className="text-lg font-bold text-gray-900">
              Reviews
              {reviewSummary.review_count > 0 && (
                <span className="text-gray-400 font-normal text-base ml-2">
                  ({reviewSummary.review_count})
                </span>
              )}
            </h2>
            {reviewSummary.average_rating > 0 && (
              <div className="flex items-center gap-1.5 ml-2">
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                <span className="text-sm font-semibold text-gray-700">{reviewSummary.average_rating.toFixed(1)}</span>
              </div>
            )}
          </div>

          {/* Submit form */}
          {canReview && !hasReviewed && (
            <form onSubmit={handleSubmitReview} className="bg-violet-50 rounded-2xl p-5 mb-6 border border-violet-100">
              <p className="font-semibold text-gray-900 mb-3">Leave a review</p>
              <div className="flex gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setReviewRating(i)}
                    className="p-0.5"
                  >
                    <Star className={`w-6 h-6 ${i <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-gray-100'}`} />
                  </button>
                ))}
              </div>
              <textarea
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
                placeholder="Share your experience with this template (optional)"
                rows={3}
                className="w-full px-4 py-2.5 border border-violet-200 bg-white rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-500 mb-3"
              />
              <button
                type="submit"
                disabled={reviewSubmitting}
                className="px-5 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50 transition-colors"
              >
                {reviewSubmitting ? 'Submitting…' : 'Submit review'}
              </button>
            </form>
          )}

          {hasReviewed && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3 text-sm text-emerald-700 font-medium flex items-center gap-2 mb-6">
              <CheckCircle className="w-4 h-4" /> You've reviewed this template.
            </div>
          )}

          {/* Review list */}
          {reviews.length === 0 ? (
            <p className="text-sm text-gray-400 py-6 text-center">No reviews yet. Be the first to review this template.</p>
          ) : (
            <div className="space-y-5">
              {reviews.map(rev => (
                <div key={rev.id} className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-400 to-fuchsia-400 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {rev.user_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{rev.user_name}</span>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} className={`w-3 h-3 ${i <= rev.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-100'}`} />
                        ))}
                      </div>
                      {rev.is_verified_purchase && (
                        <span className="text-[10px] bg-emerald-50 text-emerald-600 font-semibold px-2 py-0.5 rounded-full">Verified</span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">
                        {new Date(rev.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {rev.comment && <p className="text-sm text-gray-600 mt-1.5">{rev.comment}</p>}
                    {rev.creator_reply && (
                      <div className="mt-2 pl-3 border-l-2 border-violet-200">
                        <p className="text-xs text-violet-500 font-semibold mb-0.5">Creator replied:</p>
                        <p className="text-sm text-gray-600">{rev.creator_reply}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {reportOpen && template && (
        <ReportModal type="template" targetId={template.id} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
}
